import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/errors";
import { ShoppingBag, Sparkles, Check, Lock } from "lucide-react";

export default function Store() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const [owned, setOwned] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/store/packages").then((r) => setPacks(r.data)).catch(() => {});
    if (user) api.get("/me/purchases").then((r) => setOwned(r.data)).catch(() => {});
  }, [user]);

  const ownedIds = new Set(owned.map((o) => o.package_id));
  const isPro = user?.is_pro;

  const buy = async (pid) => {
    if (!user) { nav("/login?from=/store"); return; }
    setBusy(pid); setError("");
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: pid,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16" data-testid="store-page">
      <div className="label-mono mb-3">Store · Templates & Pro</div>
      <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tighter mb-3">
        Beat AI with the same plays we feature.
      </h1>
      <p className="text-lg max-w-2xl" style={{ color: "var(--text-dim)" }}>
        Curated template packs from the human winners in every episode — plus Pro access for the obsessed.
      </p>

      {error && <div className="tag tag-wrong mt-6 inline-block" data-testid="store-error">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {packs.map((p, i) => {
          const ownedThis = ownedIds.has(p.id);
          const isPro30 = p.type === "pro_subscription";
          const proActive = isPro30 && isPro;
          return (
            <div
              key={p.id}
              className="card overflow-hidden flex flex-col slide-up"
              style={{ animationDelay: `${i * 0.06}s`, borderColor: isPro30 ? "rgba(37,99,235,0.4)" : "var(--border)" }}
              data-testid={`pack-${p.id}`}
            >
              {p.image && (
                <div className="relative">
                  <img src={p.image} alt={p.name} className="w-full h-44 object-cover" />
                  {isPro30 && (
                    <div className="absolute top-3 left-3 tag tag-ai flex items-center gap-1.5">
                      <Sparkles size={11} /> PRO
                    </div>
                  )}
                  {ownedThis && (
                    <div className="absolute top-3 right-3 tag tag-correct flex items-center gap-1.5">
                      <Check size={11} /> Owned
                    </div>
                  )}
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="font-display font-bold text-xl mb-2 leading-tight">{p.name}</div>
                <p className="text-sm flex-1" style={{ color: "var(--text-dim)" }}>{p.description}</p>
                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <div className="font-display font-black text-3xl">${p.amount.toFixed(p.amount % 1 ? 2 : 0)}</div>
                    {p.interval && <div className="label-mono mt-1">per {p.interval} · auto-renews</div>}
                  </div>
                  {proActive ? (
                    <button className="btn btn-secondary" disabled data-testid={`pack-${p.id}-active`}>
                      <Check size={14} /> Pro active
                    </button>
                  ) : ownedThis ? (
                    <a href={`/profile`} className="btn btn-secondary" data-testid={`pack-${p.id}-download`}>
                      <Check size={14} /> Owned
                    </a>
                  ) : (
                    <button
                      className={`btn ${isPro30 ? "btn-ai" : "btn-primary"}`}
                      onClick={() => buy(p.id)}
                      disabled={busy === p.id}
                      data-testid={`pack-${p.id}-buy`}
                    >
                      {busy === p.id ? "…" : (
                        <>
                          {user ? (isPro30 ? "Subscribe" : "Buy") : <Lock size={13} />}
                          {!user && " Login & buy"} {user && <ShoppingBag size={14} />}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-16 card p-8" data-testid="store-trust">
        <div className="label-mono mb-2">Trust</div>
        <div className="font-display font-bold text-xl">Secure checkout · Powered by Stripe · 14-day refund window on packs</div>
      </div>
    </div>
  );
}
